import { Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, useToast } from '@/components/ui';
import { ROUTES } from '@/constants';
import { changeLanguage } from '@/i18n';
import { verifyOTPAsync, getOTPAsync } from '@/service/AuthenticationService';
import { convertCurrentTimeToPSTFormat } from '@/service/locationService';
import {
  useAppDispatch,
  useAppSelector,
  selectMobileNumber,
  selectCountryCode,
  selectConfig,
  setUser,
  setPlant,
  setSessionId,
  setTokens,
  setVerifyOTPData,
} from '@/store';

const RESEND_TIMER_SECONDS = 30;

export function OTPPage() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const mobileNumber = useAppSelector(selectMobileNumber);
  const countryCode = useAppSelector(selectCountryCode);
  const config = useAppSelector(selectConfig);

  const handleOTPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(e.target.value.replace(/\D/g, '').slice(0, 4));
  };

  const handleVerify = async (otpValue: string) => {
    if (otpValue.length < 4) {
      showToast(translate('login.OTPLengthMessage'), { type: 'warning' });
      return;
    }

    if (otpValue == '') {
      showToast(translate('login.OTPValidationMessage'), { type: 'warning' });
      return;
    }

    setIsLoading(true);

    const device = config?.device;
    const deviceId = device?._id ?? '';
    // const lat = parseFloat(device?.device_latitude ?? '0');
    // const lng = parseFloat(device?.device_longitude ?? '0');
    try {
      const geoFenceEnabled: boolean = config?.settings.GEOFENCE ?? false;
      //let location = null;

      console.log('geoFenceEnabled -------->', geoFenceEnabled);

      // if (geoFenceEnabled) {
      //   location = await getLocationAsync();
      // }

      //const lat = location?.latitude ?? 0;
      //const lng = location?.longitude ?? 0;
      const { result, accessToken } = await verifyOTPAsync(
        countryCode,
        mobileNumber,
        otpValue,
        0,
        0,
        deviceId
      );

      if (config?.settings.GEOFENCE && result.user == null) {
        navigate(ROUTES.restrictedArea);
        return;
      }

      if (result.user) {
        result.user.login_date_time = convertCurrentTimeToPSTFormat();
      }

      dispatch(setUser(result.user ?? null));
      dispatch(setPlant(result.plant ?? null));
      dispatch(setSessionId(result.session_id ?? null));

      const sessionTimeout = parseFloat(config?.settings.APP_SESSION_TIMEOUT ?? '0');
      const expiresIn = Date.now() + sessionTimeout * 1000;

      dispatch(setTokens({ accessToken, expiresIn }));

      if (result.settings) {
        dispatch(setVerifyOTPData({ settings: result.settings }));
      }

      // Apply user's preferred language from API response
      if (result.user?.preferred_language) {
        void changeLanguage(result.user.preferred_language);
      }

      if (result.user != null) {
        navigate(ROUTES.plantSelection, { replace: true });
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        alert(
          JSON.stringify(
            {
              message: error.message,
              stack: error.stack,
            },
            null,
            2
          )
        );
      } else {
        alert(String(error));
      }
      setOtp('');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (otp.length === 4) {
      void handleVerify(otp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  useEffect(() => {
    if (timer <= 0) return;
    const id = setInterval(() => {
      setTimer((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, [timer]);

  const handleResend = async () => {
    if (timer > 0) return;
    setIsLoading(true);
    try {
      await getOTPAsync(countryCode, mobileNumber);
      setOtp('');
      setTimer(RESEND_TIMER_SECONDS);
      showToast(translate('otp.otpSentSuccess', { phoneNumber: `${countryCode}${mobileNumber}` }));
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      showToast(message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const isInputDisabled = isLoading;

  return (
    <div className="min-h-screen flex flex-col relative">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-[1px]">
          <Loader2 className="h-10 w-10 animate-spin text-white" />
        </div>
      )}
      {/* Top blue gradient section - 60% height (matching Login page) */}
      <div
        className="absolute top-0 left-0 right-0 h-[60%]"
        style={{ background: 'linear-gradient(to right, #003366 10%, #0B1B25 100%)' }}
      />

      {/* Bottom white section - 40% height (matching Login page) */}
      <div
        className="absolute bottom-0 left-0 right-0 h-[40%]"
        style={{ background: 'linear-gradient(to right, #FFFFFF 10%, #FFFFFF 100%)' }}
      />

      {/* Content container */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Header section on blue gradient */}
        <div className="pt-6 px-6 text-left">
          <img src="/images/logo_niagara.svg" alt="Niagara" className="h-10 mb-2" />

          <h1 className="text-white text-sm md:text-lg font-light mb-0">
            {translate('login.welcome')}{' '}
            <span className="font-semibold">{translate('login.title')}</span>
          </h1>
          <p className="text-white/80 text-xs md:text-sm">{translate('login.subtitle')}</p>
        </div>

        {/* Floating OTP Card - centered on the 60/40 boundary (matching Login page) */}
        <div className="absolute top-[60%] left-0 right-0 flex justify-center px-12 md:px-12 lg:px-0 -translate-y-1/2">
          <div className="w-full md:max-w-[80%] lg:max-w-[620px] xl:max-w-[680px]">
            <div
              className="bg-white rounded-[12px] px-5 py-6 md:p-6 md:py-8"
              style={{ boxShadow: '0 4px 20px rgba(128, 128, 128, 0.8)' }}
            >
              {/* Phone Icon */}
              <div className="flex justify-center mb-[10px]">
                <img
                  src="/images/phone.svg"
                  alt=""
                  className="h-[50px] w-[50px]"
                  aria-hidden="true"
                />
              </div>

              {/* Title text */}
              <p className="text-center text-black text-xs mb-[16px] md:mb-[22px]">
                {translate('otp.enterCode', { countryCode, mobileNumber })}
              </p>

              {/* OTP Input */}
              <div className="flex justify-center mb-[16px] md:mb-[22px]">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={otp}
                  onChange={handleOTPChange}
                  disabled={isInputDisabled}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                  className="w-52 h-[50px] text-center text-2xl font-medium tracking-[0.5em] bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  aria-label={translate('otp.inputLabel')}
                  autoComplete="one-time-code"
                />
              </div>

              {/* Continue Button */}
              <div className="flex justify-center mb-[16px] md:mb-[22px]">
                <Button
                  type="button"
                  onClick={() => void handleVerify(otp)}
                  disabled={otp.length !== 4 || isInputDisabled}
                  className="w-52 md:max-w-[180px] md:w-full h-[50px] md:h-[60px] bg-[#1357A0] hover:bg-[#0f4a8a] text-white text-sm font-medium rounded-[5px]"
                >
                  {translate('otp.continue')}
                </Button>
              </div>

              {/* Resend OTP section */}
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => void handleResend()}
                  disabled={timer > 0}
                  className="text-xs font-medium transition-colors text-[#0B1B25] hover:text-[#1357A0] disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {translate('otp.resendOtp')}
                </button>
                {timer > 0 && (
                  <span className="text-xs font-medium text-[#0B1B25]">
                    (<span className="text-red-500">0:{String(timer).padStart(2, '0')}</span>)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
